import { useMemo } from "react";

import { getCurrentMagentoClient } from "@magento/clients/getClient";
import type { IGetProductsQuery, MagentoConfigurableProduct } from "@magento/types";
import { getDiscount, getDiscountBySKU, getPrice } from "@magento/utils";

import { useQuery } from "@jti/rest-api";

export type UseProductDetailsOptions = {
  sku: string;
  currentVariantSku: string;
  pageSize?: number;
  isTrial?: boolean;
};

const getConfigurableVariantSku = (
  variants: MagentoConfigurableProduct["variants"],
  currentVariantSku: string
): string => {
  const isExistingVariant = variants?.some((variant) => variant?.product?.sku === currentVariantSku);

  return isExistingVariant ? currentVariantSku : (variants?.[0]?.product?.sku ?? "");
};

export const useProductDetails = ({
  sku,
  currentVariantSku,
  pageSize = 1,
  isTrial = false,
}: UseProductDetailsOptions) => {
  const magentoClient = getCurrentMagentoClient(isTrial);

  const queryState = useQuery<IGetProductsQuery>({
    queryKey: ["GET_PRODUCT_DETAILS_BY_SKU", sku, currentVariantSku, pageSize, { isTrial }],
    queryFn: () => magentoClient.getProductsDetailsBySku([sku], pageSize),
  });

  const productData = queryState.data?.products?.items?.[0];

  const isConfigurableProduct = productData?.__typename === "ConfigurableProduct";

  const variants = isConfigurableProduct ? productData?.variants : [];

  const discount = useMemo(
    () =>
      isConfigurableProduct
        ? getDiscountBySKU(currentVariantSku, productData as MagentoConfigurableProduct)
        : getDiscount(productData),
    [productData, isConfigurableProduct, currentVariantSku]
  );

  const currentSku = isConfigurableProduct ? getConfigurableVariantSku(variants, currentVariantSku) : currentVariantSku;

  const priceInformation = useMemo(() => getPrice(sku, currentSku, productData), [productData, sku, currentSku]);

  return {
    ...queryState,
    discount,
    priceInformation,
  };
};
